import { useEffect, useRef } from 'react';
import { API } from '@unboared/react';
import { ClientDatabase } from '~/types/client/firestore/client.firestore';
import { trackLivePlayers } from '~/utils/liveGamepads';

/**
 * Publishes the live number of connected gamepads for a provisioned screen into
 * Firestore (`screenInfos.{screenID}.playerCount`), so the admin "live sessions"
 * dashboard can distinguish a loaded game with 0 players from one actually being
 * played, and show a real concurrent-player count.
 *
 * - Reads the count locally from the screen's own socket state (API.getGamepadIDs),
 *   so the production socket-server is NOT involved.
 * - Writes are debounced to coalesce connection bursts (e.g. a whole table joining
 *   at once) into a single Firestore write.
 * - A periodic reconcile re-reads the count and writes only on change. This is the
 *   source of truth: it self-heals the case where, just after a screen reload, the
 *   socket's device list has not finished syncing yet (so the initial read is 0) and
 *   already-connected gamepads never fire onConnect — without it the count stays
 *   stuck at 0 while people are actually playing. Cost stays near-zero: a stable
 *   session re-reads an in-memory map and writes nothing.
 * - Stays mounted across free-play game switches (lives in ScreenForInstance, not
 *   keyed by gameID), so the count is continuous; also covers the free-play lobby.
 * - Clears the count to 0 on teardown (game end / screen close / rotation reload).
 *
 * No-op when there is no provisioned screenID (pure anim/bar mode).
 */

const DEBOUNCE_MS = 1500;

export function useScreenPlayerCount(clientID: string, screenID?: string) {
    // Keep the latest ids without re-subscribing on every render (gotcha #1).
    const clientRef = useRef(clientID);
    clientRef.current = clientID;
    const screenRef = useRef(screenID);
    screenRef.current = screenID;

    useEffect(() => {
        if (!screenID) return undefined;

        let timer: ReturnType<typeof setTimeout> | undefined;
        let lastWritten: number | null = null;

        const write = (count: number) => {
            if (count === lastWritten) return;
            lastWritten = count;
            // Fire-and-forget; swallow transient Firestore errors (async rejection,
            // so .catch — a try/catch would NOT catch it).
            ClientDatabase.setScreenPlayerCount(clientRef.current, screenRef.current as string, count).catch(() => {});
        };

        const schedule = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => write(API.getGamepadIDs().length), DEBOUNCE_MS);
        };

        // Event-driven for snappy updates, seeded immediately (may be 0 if the device
        // list has not synced yet right after a reload), plus a periodic reconcile as the
        // reliable source of truth — all via the shared seed/subscribe/reconcile primitive.
        const teardown = trackLivePlayers({
            onSeed: () => write(API.getGamepadIDs().length),
            onConnect: schedule,
            onDisconnect: schedule,
            onDeviceBack: schedule,
            onReconcile: () => write(API.getGamepadIDs().length),
        });

        return () => {
            if (timer) clearTimeout(timer);
            teardown();
            // Clear the count on teardown (game end / screen close), unless already 0.
            // Best effort: if the page is being killed the write may not land, but the
            // screen's heartbeat then expires too, so the dashboard drops it in-window.
            if (lastWritten !== 0) {
                lastWritten = 0;
                ClientDatabase.setScreenPlayerCount(clientRef.current, screenRef.current as string, 0).catch(() => {});
            }
        };
    }, [clientID, screenID]);
}
